import React, { useEffect, useState } from 'react';
import { getUserSiteBranding } from '@/services/geo/api';
import { persistSiteBranding, resolveSidebarBrandSrc, applySiteBrandingToDocument, hasCustomSiteLogo } from '@/utils/siteBranding';

type SidebarBrandProps = {
  width: number;
  height: number;
};

type LogoShape = 'wide' | 'square';

const detectLogoShape = (naturalWidth: number, naturalHeight: number): LogoShape => {
  if (!naturalWidth || !naturalHeight) return 'wide';
  const ratio = naturalWidth / naturalHeight;
  return ratio >= 0.85 && ratio <= 1.15 ? 'square' : 'wide';
};

/**
 * 顶栏左上角品牌图：优先读缓存，登录后按当前用户解析 geo_system_settings 并刷新。
 * 宽图按 4:1 区域铺满；方图按 1:1 区域居中展示。
 */
const SidebarBrand: React.FC<SidebarBrandProps> = ({ width, height }) => {
  const [src, setSrc] = useState(() => resolveSidebarBrandSrc());
  const [customLogo, setCustomLogo] = useState(() => hasCustomSiteLogo());
  const [logoShape, setLogoShape] = useState<LogoShape>('wide');

  useEffect(() => {
    setCustomLogo(hasCustomSiteLogo());
    setLogoShape('wide');
  }, [src]);

  useEffect(() => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (!token) return;

    let cancelled = false;
    const domain = typeof window !== 'undefined' ? window.location.host : '';

    void (async () => {
      try {
        const res = await getUserSiteBranding(domain);
        if (cancelled || res?.code !== 1 || !res.data) return;

        persistSiteBranding({
          site_name: res.data.site_name,
          site_logo: res.data.site_logo,
          site_favicon: res.data.site_favicon,
          icp: res.data.icp,
          copyright: res.data.copyright,
        });
        setSrc(resolveSidebarBrandSrc(res.data));
        setCustomLogo(hasCustomSiteLogo(res.data));
        applySiteBrandingToDocument(res.data);
      } catch {
        // 保持缓存/默认图
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const isSquare = customLogo && logoShape === 'square';

  return (
    <div
      className={`geo-sidebar-brand-wrap ${isSquare ? 'geo-sidebar-brand-wrap--square' : 'geo-sidebar-brand-wrap--wide'}`}
      style={{
        width,
        maxWidth: '100%',
        height,
        maxHeight: height,
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: isSquare ? 'center' : 'flex-start',
        padding: 0,
        margin: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
        lineHeight: 0,
      }}
    >
      <img
        src={src}
        alt="站点品牌"
        onLoad={(event) => {
          if (!hasCustomSiteLogo()) {
            setLogoShape('wide');
            return;
          }
          const img = event.currentTarget;
          setLogoShape(detectLogoShape(img.naturalWidth, img.naturalHeight));
        }}
        style={{
          width: isSquare ? height : '100%',
          height: isSquare ? height : '100%',
          maxWidth: isSquare ? height : '100%',
          maxHeight: '100%',
          objectFit: isSquare ? 'contain' : 'cover',
          objectPosition: isSquare ? 'center' : 'left center',
          display: 'block',
          margin: 0,
          padding: isSquare ? 6 : 0,
          boxSizing: 'border-box',
          flex: '0 0 auto',
        }}
      />
    </div>
  );
};

export default SidebarBrand;
